import { motion } from "framer-motion";
import { getFAIconClass } from "@juiceforward/narrative-schema/icon-utils";
import { Icon } from "./Icon";
import type { TOCSection } from "./tocUtils";

interface TableOfContentsProps {
  sections: TOCSection[];
  selectedSections: Set<string>;
  onSelectionChange: (sectionId: string, isSelected: boolean) => void;
  onContinue: () => void;
  selectSections?: boolean; // If false or undefined, show list without selection; if true, enable selection
}

export function TableOfContents({
  sections,
  selectedSections,
  onSelectionChange,
  selectSections = false, // Default to false (read-only navigation)
  onContinue,
}: TableOfContentsProps) {
  const handleToggle = (sectionId: string) => {
    // Only allow toggling if selectSections is enabled
    if (!selectSections) return;

    const isCurrentlySelected = selectedSections.has(sectionId);
    onSelectionChange(sectionId, !isCurrentlySelected);
  };

  const handleSelectAll = () => {
    // Only allow select all if selectSections is enabled
    if (!selectSections) return;

    sections.forEach((section) => {
      if (!selectedSections.has(section.id)) {
        onSelectionChange(section.id, true);
      }
    });
  };

  const hasSelection = selectedSections.size > 0;
  const isReadOnly = !selectSections;

  return (
    <div className="w-full space-y-10">
      {/* Prompt text - only show in selection mode */}
      {!isReadOnly && !hasSelection && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-left mb-2"
        >
          <p className="text-white/70 text-sm">
            Select at least one
            <button
              onClick={handleSelectAll}
              className="ml-2 text-white underline hover:text-white/90 cursor-pointer"
            >
              Select All
            </button>
          </p>
        </motion.div>
      )}

      {/* Section checkboxes or list items */}
      <div className={isReadOnly ? "space-y-2" : "space-y-3"}>
        {sections.map((section, index) => {
          const isSelected = selectedSections.has(section.id);
          const iconClass = getFAIconClass(section.icon);

          return (
            <motion.button
              key={section.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => handleToggle(section.id)}
              disabled={isReadOnly}
              className={`w-full flex items-center text-left transition-all duration-300 ${
                isReadOnly
                  ? "gap-2 py-2 px-0 cursor-default"
                  : isSelected
                    ? "gap-3 py-3 px-4 bg-black/60 hover:bg-black/70 rounded-2xl backdrop-blur-sm cursor-pointer shadow-lg"
                    : "gap-3 py-3 px-4 bg-white/5 hover:bg-white/10 rounded-2xl backdrop-blur-sm opacity-60 cursor-pointer"
              }`}
            >
              {/* Show icon if available, otherwise show checkbox in selection mode */}
              {iconClass ? (
                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                  <Icon icon={section.icon} className="text-white text-lg" />
                </div>
              ) : !isReadOnly ? (
                <motion.div
                  initial={false}
                  animate={{
                    scale: isSelected ? 1 : 0.8,
                    opacity: isSelected ? 1 : 0.4,
                  }}
                  className={`flex-shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center ${
                    isSelected
                      ? "border-white bg-white"
                      : "border-white/40 bg-transparent"
                  }`}
                >
                  {isSelected && (
                    <motion.svg
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 30,
                      }}
                      className="w-3 h-3 text-blue-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </motion.svg>
                  )}
                </motion.div>
              ) : null}

              {/* Section label */}
              <span
                className={`text-lg text-white ${
                  isReadOnly
                    ? "font-bold"
                    : isSelected
                      ? "font-bold"
                      : "font-medium"
                }`}
              >
                {section.label}
              </span>
            </motion.button>
          );
        })}
      </div>

      {/* Continue button */}
      <motion.button
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: sections.length * 0.1 + 0.2 }}
        onClick={onContinue}
        disabled={!isReadOnly && !hasSelection}
        className={`w-full py-4 px-6 rounded-full font-semibold text-lg shadow-lg transition-all duration-300 ${
          isReadOnly || hasSelection
            ? "bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white transform hover:scale-[1.02] cursor-pointer"
            : "bg-white/10 text-white/40 cursor-not-allowed"
        }`}
      >
        {isReadOnly
          ? "Continue"
          : hasSelection
            ? `Continue with ${selectedSections.size} ${selectedSections.size === 1 ? "Insight" : "Insights"}`
            : "Select Insights to continue"}
      </motion.button>
    </div>
  );
}
